// Ensure the existing frontend code remains unchanged, except for functionality updates.
import React, { useState, useEffect } from "react";
import { buildApiUrl } from '../config';
import "./Payments.css";

const Payments = () => {
  const [companies, setCompanies] = useState([]); // List of companies
  const [suppliers, setSuppliers] = useState([]); // List of suppliers
  const [selectedEntity, setSelectedEntity] = useState(""); // Selected company or supplier
  const [currentBalance, setCurrentBalance] = useState(0); // Current balance
  const [paymentAmount, setPaymentAmount] = useState(""); // Payment amount entered
  const [paymentDate, setPaymentDate] = useState(""); // Payment date
  const [paymentMethod, setPaymentMethod] = useState(""); // Payment method
  const [paymentNotes, setPaymentNotes] = useState(""); // Notes
  const [entityType, setEntityType] = useState("company"); // Entity type: "company" or "supplier"
  const [message, setMessage] = useState(""); // Success/Error message
  const [walkinCustomers, setWalkinCustomers] = useState([]); // List of walk-in customers
  const [overtimeWorkers, setOvertimeWorkers] = useState([]); // List of workers with overtime
  const [vatInvoices, setVatInvoices] = useState([]);
  const [vatSummary, setVatSummary] = useState(null);

  // Fetch companies and suppliers
  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const companiesResponse = await fetch(
          buildApiUrl("api/companies")
        );
        const suppliersResponse = await fetch(
          buildApiUrl("api/suppliers")
        );
        const companiesData = await companiesResponse.json();
        const suppliersData = await suppliersResponse.json();
        setCompanies(companiesData);
        setSuppliers(suppliersData);
      } catch (error) {
        console.error("Error fetching entities:", error);
      }
    };

    fetchEntities();
  }, []);

  // Add useEffect to fetch walk-in customers with credit sales
  useEffect(() => {
    if (entityType === "walkin") {
      fetch(buildApiUrl("api/walkin-customers/credit"))
        .then((res) => res.json())
        .then((data) => setWalkinCustomers(data.customers || []))
        .catch((err) => setWalkinCustomers([]));
    }
  }, [entityType]);

  // Add useEffect to fetch overtime workers
  useEffect(() => {
    if (entityType === "overtime") {
      fetch(buildApiUrl("api/overtime/workers"))
        .then((res) => res.json())
        .then((data) => setOvertimeWorkers(data.data || []))
        .catch((err) => setOvertimeWorkers([]));
    }
  }, [entityType]);

  // VAT invoices for payment dropdown
  useEffect(() => {
    if (entityType === "vat") {
      fetch(buildApiUrl("api/vat/invoices-for-payment"))
        .then((res) => res.json())
        .then((data) => setVatInvoices(data.data || []))
        .catch(() => setVatInvoices([]));
    } else {
      setVatInvoices([]);
      setVatSummary(null);
    }
  }, [entityType]);

  const handleVatInvoiceChange = async (invoiceNumber) => {
    setSelectedEntity(invoiceNumber);
    setMessage("");
    setVatSummary(null);
    setCurrentBalance(0);
    if (!invoiceNumber) return;
    try {
      const res = await fetch(
        buildApiUrl(`api/vat/invoice-summary/${encodeURIComponent(invoiceNumber)}`)
      );
      const data = await res.json();
      if (data.success) {
        setVatSummary(data.data);
        setCurrentBalance(data.data.remaining || 0);
      }
    } catch (error) {
      console.error("Error fetching VAT invoice summary:", error);
    }
  };

  const handleEntityChange = async (entityId) => {
    setSelectedEntity(entityId);
    setMessage("");

    if (entityId) {
        try {
            const endpoint =
                entityType === "company"
                    ? `/api/company/balance/${entityId}`
                    : `/api/supplier/balance/${entityId}`; // Use SupplierID directly

            const response = await fetch(buildApiUrl(endpoint.substring(1))); // Remove leading slash
            const data = await response.json();

            if (response.ok) {
                setCurrentBalance(data.balance || 0);
            } else {
                console.error("Failed to fetch balance:", data.error);
                setCurrentBalance(0); // Default balance if API fails
            }
        } catch (error) {
            console.error("Error fetching balance:", error);
            setCurrentBalance(0); // Default balance in case of an error
        }
    } else {
        setCurrentBalance(0); // Reset balance if no entity selected
    }
};

  // Handle payment submission
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!selectedEntity) {
      alert(
        `Please select a ${
          entityType === "walkin"
            ? "walk-in customer"
            : entityType === "overtime"
              ? "worker"
              : entityType === "vat"
                ? "invoice"
                : entityType
        }!`
      );
      return;
    }

    if (!paymentDate) {
      alert("Please select a payment date!");
      return;
    }

    if (!paymentMethod) {
      alert("Please select a payment method!");
      return;
    }

    if (!paymentAmount || isNaN(paymentAmount) || parseFloat(paymentAmount) <= 0) {
      alert("Please enter a valid payment amount!");
      return;
    }
    
    try {
      let response;

      if (entityType === "vat") {
        response = await fetch(buildApiUrl("api/vat/record-payment"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            invoiceNumber: selectedEntity,
            amount: parseFloat(paymentAmount),
            paymentMethod: paymentMethod,
            notes: paymentNotes,
            date: paymentDate,
          }),
        });
      } else if (entityType === "overtime") {
        // Handle overtime settlement
        response = await fetch(
          buildApiUrl("api/overtime/settlement/add"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              WorkerName: selectedEntity,
              amount: parseFloat(paymentAmount),
              paymentMethod: paymentMethod,
              notes: paymentNotes,
              date: paymentDate,
            }),
          }
        );
      } else if (entityType === "walkin") {
        // Handle walk-in customer payment
        response = await fetch(
          buildApiUrl("api/customer/payment/add"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              customerName: selectedEntity,
              amount: parseFloat(paymentAmount),
              paymentMethod: paymentMethod,
              notes: paymentNotes,
              date: paymentDate,
            }),
          }
        );
      } else {
        // Handle company/supplier payment
        response = await fetch(
          buildApiUrl(`api/${entityType}/payments/add`),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              entityId: selectedEntity,
              amount: parseFloat(paymentAmount),
              paymentMethod: paymentMethod,
              notes: paymentNotes,
              date: paymentDate,
            }),
          }
        );
      }

      const data = await response.json();
      if (response.ok) {
        setMessage("Payment successfully recorded!");
        alert("Payment submitted successfully!");
        setPaymentAmount("");
        setPaymentDate("");
        setPaymentMethod("");
        setPaymentNotes("");
        
        // Refresh the balance immediately after successful payment
        if (entityType === "vat") {
          await handleVatInvoiceChange(selectedEntity);
          // refresh dropdown statuses
          const listRes = await fetch(buildApiUrl("api/vat/invoices-for-payment"));
          const listData = await listRes.json();
          setVatInvoices(listData.data || []);
        } else if (entityType === "walkin") {
          const res = await fetch(buildApiUrl(`api/customer/balance/${encodeURIComponent(selectedEntity)}`));
          const balData = await res.json();
          setCurrentBalance(balData.balance || 0);
        } else if (entityType === "overtime") {
          const res = await fetch(buildApiUrl(`api/overtime/balance/${encodeURIComponent(selectedEntity)}`));
          const balData = await res.json();
          setCurrentBalance(balData.balance || 0);
        } else {
          await handleEntityChange(selectedEntity);
        }
      } else {
        setMessage(`Error recording payment: ${data.error || 'Please try again.'}`);
      }
    } catch (error) {
      console.error("Payment submission error:", error);
      setMessage("Error recording payment. Please try again.");
    }
  };

  return (
    <div className="payments-container">
      <h1>Record Payment</h1>
      <div className="radio-group">
        <label
          className={`radio-label ${
            entityType === "company" ? "selected" : ""
          }`}
        >
          <input
            type="radio"
            name="entityType"
            value="company"
            checked={entityType === "company"}
            onChange={() => {
              setEntityType("company");
              setSelectedEntity("");
              setCurrentBalance(0);
              setVatSummary(null);
              setMessage("");
            }}
          />
          Company
        </label>
        <label
          className={`radio-label ${
            entityType === "supplier" ? "selected" : ""
          }`}
        >
          <input
            type="radio"
            name="entityType"
            value="supplier"
            checked={entityType === "supplier"}
            onChange={() => {
              setEntityType("supplier");
              setSelectedEntity("");
              setCurrentBalance(0);
              setVatSummary(null);
              setMessage("");
            }}
          />
          Supplier
        </label>
        <label className={`radio-label ${entityType === "walkin" ? "selected" : ""}`}>
          <input
            type="radio"
            name="entityType"
            value="walkin"
            checked={entityType === "walkin"}
            onChange={() => {
              setEntityType("walkin");
              setSelectedEntity("");
              setCurrentBalance(0);
              setVatSummary(null);
              setMessage("");
            }}
          />
          Walk-ins Credit
        </label>
        <label className={`radio-label ${entityType === "overtime" ? "selected" : ""}`}>
          <input
            type="radio"
            name="entityType"
            value="overtime"
            checked={entityType === "overtime"}
            onChange={() => {
              setEntityType("overtime");
              setSelectedEntity("");
              setCurrentBalance(0);
              setVatSummary(null);
              setMessage("");
            }}
          />
          Overtime Payment
        </label>
        <label className={`radio-label ${entityType === "vat" ? "selected" : ""}`}>
          <input
            type="radio"
            name="entityType"
            value="vat"
            checked={entityType === "vat"}
            onChange={() => {
              setEntityType("vat");
              setSelectedEntity("");
              setCurrentBalance(0);
              setVatSummary(null);
              setMessage("");
            }}
          />
          VAT
        </label>
      </div>

      <div className="form-group">
        <label htmlFor="entity">
          Select{" "}
          {entityType === "company"
            ? "Company"
            : entityType === "supplier"
              ? "Supplier"
              : entityType === "walkin"
                ? "Walk-in Customer"
                : entityType === "vat"
                  ? "Invoice"
                  : "Worker"}
          :
        </label>
        {entityType === "vat" ? (
          <select
            id="vat-invoice"
            value={selectedEntity}
            onChange={(e) => handleVatInvoiceChange(e.target.value)}
          >
            <option value="">-- Select Invoice Number --</option>
            {vatInvoices.map((inv) => (
              <option key={inv.sr_no} value={inv.invoice_number}>
                {inv.invoice_number} | {inv.company_name} | {inv.vatStatus} | Rem: {Number(inv.remaining || 0).toFixed(2)}
              </option>
            ))}
          </select>
        ) : entityType === "walkin" ? (
          <select
            id="walkin-customer"
            value={selectedEntity}
            onChange={async (e) => {
              setSelectedEntity(e.target.value);
              setMessage("");
              if (e.target.value) {
                // Fetch balance for walk-in customer
                const res = await fetch(buildApiUrl(`api/customer/balance/${encodeURIComponent(e.target.value)}`));
                const data = await res.json();
                setCurrentBalance(data.balance || 0);
              } else {
                setCurrentBalance(0);
              }
            }}
          >
            <option value="">-- Select Walk-in Customer --</option>
            {walkinCustomers.map((c) => (
              <option key={c.customer_id} value={c.name}>{c.name}</option>
            ))}
          </select>
        ) : entityType === "overtime" ? (
          <select
            id="overtime-worker"
            value={selectedEntity}
            onChange={async (e) => {
              setSelectedEntity(e.target.value);
              setMessage("");
              if (e.target.value) {
                // Fetch balance for overtime worker
                const res = await fetch(buildApiUrl(`api/overtime/balance/${encodeURIComponent(e.target.value)}`));
                const data = await res.json();
                setCurrentBalance(data.balance || 0);
              } else {
                setCurrentBalance(0);
              }
            }}
          >
            <option value="">-- Select Worker --</option>
            {overtimeWorkers.map((worker, index) => (
              <option key={index} value={worker.WorkerName}>{worker.WorkerName}</option>
            ))}
          </select>
        ) : (
          <select
            id="entity"
            value={selectedEntity}
            onChange={(e) => handleEntityChange(e.target.value)}
          >
            <option value="">
              -- Select {entityType === "company" ? "Company" : entityType === "supplier" ? "Supplier" : "Walk-in Customer"} --
            </option>
            {(entityType === "company" ? companies : entityType === "supplier" ? suppliers : walkinCustomers).map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {selectedEntity && entityType !== "vat" && (
        <div className="balance-box">
          <h3>Current Balance</h3>
          <p>AED {currentBalance.toFixed(2)}</p>
        </div>
      )}

      {selectedEntity && entityType === "vat" && vatSummary && (
        <div className="balance-box">
          <h3>VAT Payment to Govt</h3>
          <p style={{ margin: "6px 0" }}>
            Invoice VAT: AED {Number(vatSummary.vatAmount || 0).toFixed(2)}
          </p>
          <p style={{ margin: "6px 0" }}>
            Expense VAT
            {vatSummary.periodFrom && vatSummary.periodTo
              ? ` (${vatSummary.periodFrom} → ${vatSummary.periodTo})`
              : " (no period on invoice)"}
            : AED {Number(vatSummary.expenseVatTotal || 0).toFixed(2)}
          </p>
          <p style={{ margin: "6px 0" }}>
            Already paid: AED {Number(vatSummary.paidTotal || 0).toFixed(2)}
          </p>
          <p style={{ margin: "8px 0 0", fontWeight: 700 }}>
            Remaining: AED {Number(vatSummary.remaining || 0).toFixed(2)}
            {vatSummary.remaining < 0 ? " (Paid / claim)" : ""}
          </p>
          <p style={{ margin: "4px 0 0", fontSize: 13 }}>Payee: Govt</p>
        </div>
      )}

      {selectedEntity && (
        <form onSubmit={handlePaymentSubmit} className="payment-form">
          <div className="form-row">
            <div className="form-group-inline">
              <label htmlFor="date">Date:</label>
              <input
                type="date"
                id="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                required
              />
            </div>

            <div className="form-group-inline">
              <label htmlFor="payment-method">Payment Method:</label>
              <select
                id="payment-method"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                required
              >
                <option value="">-- Select Payment Method --</option>
                <option value="cash">Cash</option>
                <option value="bank-transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="pos">POS</option>
              </select>
            </div>

            <div className="form-group-inline">
              <label htmlFor="notes">Notes:</label>
              <textarea
                id="notes"
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Enter notes (optional)"
              ></textarea>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group-inline full-width">
              <label htmlFor="amount">Payment Amount:</label>
              <input
                type="text"
                id="amount"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter payment amount"
                required
              />
            </div>
          </div>

          <div className="form-row centered">
            <button type="submit" className="submit-button">
              Record Payment
            </button>
          </div>
        </form>
      )}

      {message && <p className="message">{message}</p>}
    </div>
  );
};

export default Payments;
